import {Flag} from 'platform/components';
import {Box, Center, Icon, Show, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {SaleVehiclePhotoType} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getPhotoGuideSilhouettePart} from '../utils/getPhotoGuideSilhouettePart';
import {getSaleVehiclePhotoTypeLabel} from '../utils/getSaleVehiclePhotoTypeLabel';
import {PhotoGuideSlotSilhouette} from './PhotoGuideSlotSilhouette';

interface PhotoGuideSlotPlaceholderProps extends TestIdProps {
  photoType: SaleVehiclePhotoType;
  isRequired: boolean;
  isDropTarget?: boolean;
}

/**
 * What an empty slot shows: the car silhouette with the slot's view marked (or a generic photo
 * icon for views the silhouette has not got), the Photo Type and the advisory "required" badge.
 */
export function PhotoGuideSlotPlaceholder(props: PhotoGuideSlotPlaceholderProps) {
  const part = getPhotoGuideSilhouettePart(props.photoType);

  return (
    <Box
      ratio="4 / 3"
      width="100%"
      overflow="hidden"
      borderRadius="small"
      border="2px dashed"
      borderColor={props.isDropTarget ? 'palettes.blue.60.100' : 'general.separator'}
      backgroundColor="palettes.neutral.10.100"
      data-testid={suffixTestId('photoGuideSlotPlaceholder', props)}
    >
      <Center width="100%" height="100%">
        <VStack spacing={1} align="center" width="100%">
          <Show when={part}>
            {(silhouettePart) => (
              <Box width="60%">
                <PhotoGuideSlotSilhouette part={silhouettePart} isRequired={props.isRequired} />
              </Box>
            )}
          </Show>
          <Show when={isNil(part)}>
            <Icon value="image/photo_camera" color="text.tertiary" size={6} />
          </Show>
          <Text size="xSmall" align="center" data-testid={suffixTestId('photoType', props)}>
            {getSaleVehiclePhotoTypeLabel(props.photoType)}
          </Text>
          <Show when={props.isRequired}>
            <Flag
              label={i18n.t('general.labels.required')}
              colorScheme="orange"
              isSubtle
              size="small"
              data-testid={suffixTestId('required', props)}
            />
          </Show>
        </VStack>
      </Center>
    </Box>
  );
}
